import { EventApi } from "@fullcalendar/core";
import { format, isSameDay, subDays } from "date-fns";
import React, { useContext } from "react";
import './assets/event.scss'
import { ChangeStatus, ECalendarStatus, IEvent } from "../../../interfaces/event";
import { FontIcon, IconButton, Spinner, SpinnerSize, TooltipHost } from "@fluentui/react";
import { truncate } from "../../../helpers/truncate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBed, faBell, faExclamationTriangle, faChevronRight, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { shiftsContext } from "../../../providers/shifts";
import { buildCrmUrl } from "../../../helpers/crmUrlBuilder";
import { AvailabilityType, ContractedHoursType, IContractedHours, IResourceData, JobType, JobTypeLabels } from "../../../interfaces/resource";
import { getSchedulerRowMetaFromResource } from "../../../helpers/scheduler/groupedResources";

interface IPropsWarningIcons {
    id?: string
}

const OvertimeIcon = ({ id }: IPropsWarningIcons) => {
    return (
        <TooltipHost content={'Overtime'} key={`overtimeIcon_${id}`}>
            <FontIcon
                iconName="Clock"
                style={{ fontSize: '1em', color: 'var(--color-warningIcon)' }}
            />
        </TooltipHost>
    )

}

const ResourceNotAvailableIcon = ({ id }: IPropsWarningIcons) => {
    return (
        <TooltipHost content={'Resource not available'} key={`resourceNotAvailableIcon_${id}`}>
            <FontIcon
                iconName="IncidentTriangle"
                style={{ fontSize: '1em', color: 'var(--color-errorIcon)' }}
            />
        </TooltipHost>
    )

}

const MissingSkillsIcon = ({ id }: IPropsWarningIcons) => {
    return (
        <TooltipHost content={'Missing/Expired Skills'} key={`missingSkillsIcon_${id}`}>
            <FontIcon
                iconName="Library"
                style={{ fontSize: '1em', color: 'var(--color-warningIcon)' }}
            />
        </TooltipHost>
    )

}

const SleepoverActivePortionMissingIcon = ({ id }: IPropsWarningIcons) => {
    return (
        <TooltipHost content={'Active portion of the sleepover shift missing'} key={`sleepoverActivePortionMissingIcon_${id}`}>
            <FontAwesomeIcon icon={faBed} style={{ fontSize: '1em', color: 'var(--color-warningIcon)' }} />
        </TooltipHost>
    )

}

const MissingTravelCoordinatesBadge = ({ id, siteName }: IPropsWarningIcons & { siteName?: string | null }) => {
    const tooltip = siteName
        ? `Travel will not be calculated because "${siteName}" does not have latitude/longitude configured.`
        : 'Travel will not be calculated because this site does not have latitude/longitude configured.';

    return (
        <TooltipHost content={tooltip} key={`missingTravelCoordinatesBadge_${id}`}>
            <div className="eventWarningBadge eventWarningBadge--travel">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>No travel</span>
            </div>
        </TooltipHost>
    );
}

const JobBiddingIcon = ({ id }: IPropsWarningIcons) => {
    return (
        <div key={`jobBiddingIcon_${id}`}>
            <TooltipHost content={'Job Bidding'} key={`jobBiddingIcon_${id}`}>
                <FontAwesomeIcon icon={['fas', 'bell']} color={'#f37037'} />
            </TooltipHost>
        </div>
    )

}

const TravelEventRender = ({ event }: { event: EventApi }) => {
    const label = String(event.extendedProps.travelLabel ?? event.title ?? '');
    const tooltip = String(event.extendedProps.travelTooltip ?? label);
    const labelAlign = event.extendedProps.labelAlign === 'end' ? 'end' : 'start';
    const overflowMinutes = Number(event.extendedProps.overflowMinutes ?? 0);

    return (
        <TooltipHost content={tooltip}>
            <div className={`travelEventContainer ${labelAlign === 'end' ? 'align-end' : 'align-start'} ${overflowMinutes > 0 ? 'overflow' : ''}`}>
                <div className="travelEventLine" />
                <div className="travelEventLabel">{label}</div>
            </div>
        </TooltipHost>
    );
};


interface EventRenderProps {
    event: EventApi,
    getShift: (eventId: string) => IEvent | undefined,
    isDragging: boolean,
    checkCharacteristics: (eventId: string, resourceId: string) => boolean,
    getResourceData: (id: string) => IResourceData,
    checkIsModified: (originalResourceID: string, resourceId: string) => ChangeStatus,
    eventClick: (event: EventApi) => void,
    isCancellationReviewPending: (shift: IEvent | undefined) => boolean
}

const EventRender: React.FC<EventRenderProps> = ({
    event,
    getShift,
    isDragging,
    checkCharacteristics,
    getResourceData,
    checkIsModified,
    eventClick,
    isCancellationReviewPending
}) => {
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(true);
    const scrollDivRef = React.useRef<HTMLDivElement | null>(null);
    const [showType, setShowType] = React.useState(false);

    React.useEffect(() => {
        const el = scrollDivRef.current;
        if (!el) return;
        checkScroll();
        el.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);
        return () => {
            el.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, []);

    // event check scroll for contracted hours
    const checkScroll = () => {
        const el = scrollDivRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    // event handle scroll left and right for contracted hours
    const handleScroll = (direction: 'left' | 'right') => {
        const el = scrollDivRef.current;
        if (el) {
            const scrollAmount = 120;
            el.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
            setTimeout(checkScroll, 300);
        }
    };

    // Resource Details
    const eventResources = event.getResources()[0];
    const eventResourceMeta = getSchedulerRowMetaFromResource(eventResources);
    const actualEventResourceId = eventResourceMeta.actualResourceId ?? eventResources.id;

    if (event.display === 'travel' || event.extendedProps?.travelKind) {
        return <TravelEventRender event={event} />;
    }

    if (event.display === 'unavailable') {
        return (
            <>
            <div role="button" className="eventContainer unavailable border-solid">
            <div className="warning-icons-container">
            </div>
            <div className="eventDescription">
                <p>
                    <strong>
                        {event.allDay ? format(event.start as Date, 'dd/MM/yyyy') :
                            isSameDay(event.start as Date, event.end as Date) ? (format(event.start as Date, 'dd/MM/yyyy') + ' ' + format(event.start as Date, 'h:mma') + ' - ' + format(event.end as Date, 'h:mma')) :
                                (format(event.start as Date, 'dd/MM/yyyy h:mma') + ' - ' + format(event.end as Date, 'dd/MM/yyyy h:mma'))
                        }
                    </strong>
                </p>
                <p><strong>On Leave</strong></p>
            </div>
        </div>
            </>
        )
    } 
    

    // display contracted hours for the day
    if (event.display === 'contractedHours') {
        const contractedHoursByCell = event.extendedProps.events ?? [];
        return (
            <>
                {contractedHoursByCell.length > 0 && (
                    <div
                        className="eventContractedHours contracted-hours-event"
                        onClick={() => setShowType((prev) => !prev)}
                    >
                        {canScrollLeft && (
                            <button
                                className="arrow-btn left"
                                onClick={e => { e.stopPropagation(); handleScroll('left'); }}
                                aria-label="Scroll left"
                                type="button"
                            >
                                <FontAwesomeIcon icon={['fas', 'chevron-left']} color={'#ffffff'} fontSize={'12px'} />
                            </button>
                        )}
                        <div
                            className="eventContractedHours-scroll"
                            ref={scrollDivRef}
                        >
                            {contractedHoursByCell
                                .sort((a: IContractedHours, b: IContractedHours) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
                                .map((ele: IContractedHours, idx: number) => {
                                    let color = '';
                                    let typeLabel = '';
                                    if (ele.type === ContractedHoursType.Active) {
                                        color = '#B9D6F0';
                                        typeLabel = 'Active';
                                    } else if (ele.type === ContractedHoursType.Passive) {
                                        color = '#BFB1C1';
                                        typeLabel = 'Passive';
                                    } else if (ele.type === ContractedHoursType.UnpaidBreak) {
                                        color = '#FDE4AF';
                                        typeLabel = 'Break';
                                    }
                                    return (
                                        <div
                                            key={idx}
                                            style={{background: color}}
                                            className="contracted-hours-item"
                                        >
                                            {showType && (
                                                <div style={{ fontSize: 10, marginTop: 2 }}>
                                                    {typeLabel}
                                                </div>
                                            )}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                                <span>{format(new Date(ele.startDate), 'HH:mm')}</span>
                                                <span> - </span>
                                                <span>{format(new Date(ele.endDate), 'HH:mm')}</span>
                                            </div>
                                        </div>
                                    )
                                })}
                        </div>
                        {canScrollRight && (
                            <button
                                className="arrow-btn right"
                                onClick={e => { e.stopPropagation(); handleScroll('right'); }}
                                aria-label="Scroll right"
                                type="button"
                            >
                             <FontAwesomeIcon icon={['fas', 'chevron-right']} color={'#ffffff'} fontSize={'12px'} />
                            </button>
                        )}
                    </div>
                )}
            </>
        );
    }

    // display warning icon if no contracted hours for the day
    if (event.display === 'noContractedHours') {
        if (actualEventResourceId != '0') {
        return (
            <>
            <div style={{display: 'flex', justifyContent: 'right', alignItems: 'center'}}>
            <TooltipHost content={'Contracted hours missing for this day'} key={`emptyCellWarningIcon_${actualEventResourceId}`}>
            <FontAwesomeIcon 
                icon={['fas', 'triangle-exclamation']}
                style={{ 
                    fontSize: '1em', 
                    color: '#f37037',
                }} 
            />
        </TooltipHost>
            </div>
            </>
        );
        } else {
            return <div></div>
        }
    }

    if (event.display === 'background' || event?.extendedProps.availabilityType != null) {
        let endDate = event.end as Date;
        if (event?.allDay && !isSameDay(event.start as Date, event.end as Date)) {
            endDate = subDays(event.end as Date, 1);
        }
        return (
            <>
        <div role="button" className={"eventContainer border-solid " + (event.extendedProps?.availabilityType == AvailabilityType.preferred ? 'preferred' : 'unavailable')}>
            <div className="warning-icons-container">
            </div>
            <div className="eventDescription">
                <p>
                    <strong>
                        {
                            (!event.start && !event.end) ? 'Invalid start/end time' :
                                (event.start && !event.end) ? format(event.start as Date, 'dd/MM/yyyy') : //single event - all day
                                    isSameDay(event.start as Date, event.end as Date) ? //single event with start and end time
                                        format(event.start as Date, 'dd/MM/yyyy') + ' ' +
                                        (format(event.start as Date, 'h:mma') + ' - ' + format(event.end as Date, 'h:mma'))
                                        : event.allDay ? (format(event.start as Date, 'dd/MM/yyyy') + ' - ' + format(endDate as Date, 'dd/MM/yyyy')) //multiple day event - all day
                                            : (format(event.start as Date, 'dd/MM/yyyy') + ' - ' + format(endDate as Date, 'dd/MM/yyyy') + ' ' +
                                                format(event.start as Date, 'h:mma') + ' - ' + format(endDate as Date, 'h:mma'))}
                    </strong>
                </p>
                <p><strong>{event.title}</strong></p>
            </div>
        </div>
        </>
        )
    }

    const shift = getShift(event.id);
    const hasMissingTravelCoordinates =
        !!shift?.siteLocationDetails?.id &&
        (shift.siteLocationDetails.latitude == null || shift.siteLocationDetails.longitude == null);
    
    if (event.extendedProps.loading || shift?.validating) {
        return (
            // Show a clear "busy" state and block drag while validation runs.
            <div className="eventContainer loading not-draggable" title="Validating, drag disabled">
                <Spinner size={SpinnerSize.medium} />
                {/* Keep text short so it fits inside small cards */}
                <span className="event-loading-text">Validating...</span>
            </div>
        )
    }


    

    // Resource Data
    const resourceData = getResourceData(actualEventResourceId);

    // Calendar status of the event
    const calendarStatus = event.extendedProps.calendarStatus

    const getStatus = (): string => {
        if (shift?.resourceId !== actualEventResourceId) {
            // Event will be unassigned if dropped
            if (isResourceUnassigned()) {
                return 'info';
            }

            // Event will be overtime if dropped
            if (isOvertime()) {
                return 'warning';
            }

            // Event will be missing characteristics if dropped
            if (isResourceMissingCharacteristics()) {
                return 'warning';
            }

            // Event will have no issues if dropped
            else {
                return 'success';
            }
        } else {
            // Error Status
            if (calendarStatus === ECalendarStatus.error) return 'error'

            // Unassigned
            if (shift?.resourceId === "0") return 'info'

            // Overtime
            if (resourceData.allSitesHoursWorked > resourceData.maxHoursAllowedPerPayroll) return 'warning';

            // Issue Status
            if (calendarStatus === ECalendarStatus.issue) return 'warning';

            // Event will be missing characteristics if dropped
            if (isResourceMissingCharacteristics()) {
                return 'warning';
            }

            // Assigned, no issues
            return 'success'
        }
    }

    const getBorderStyle = () => {
        let cssClass = isModified() ? 'border-dotted' : 'border-solid';
        if (isCancellationReviewPending(shift))
            cssClass = cssClass + ' border-solid-cancel-request';
        return cssClass;
    }

    const getWarningIcons = (): React.ReactNode => {
        if (event.getResources().length === 0) {
            return;
        }


        const icons: JSX.Element[] = [];

        if (hasMissingTravelCoordinates) {
            icons.push(
                <MissingTravelCoordinatesBadge
                    id={event.id}
                    key={`missing-travel-coordinates-badge_${event.id}`}
                    siteName={shift?.siteLocationDetails?.name}
                />
            );
        }

        // Unassigned Check
        if (isNotDefined()) {
            return icons.length > 0 ? <div className={'eventIcons'}>{icons}</div> : [];
        }

        // Missing Skills Check
        if (!checkCharacteristics(event.id, actualEventResourceId)) {
            icons.push(<MissingSkillsIcon id={event.id} key={`missing-skills-icon_${event.id}`} />);
        }

        // Not Available Check
        if (event.extendedProps.unavailable) {
            icons.push(<ResourceNotAvailableIcon id={event.id} key={`error-icon_${event.id}`} />);
        }

        // Sleepover active portion warning.
        if (hasSleepoverActivePortionMissing()) {
            icons.push(<SleepoverActivePortionMissingIcon id={event.id} key={`sleepover-active-portion-missing-icon_${event.id}`} />);
        }

        // Overtime Check
        if (hasExplicitOvertimeAlert()) {
            icons.push(<OvertimeIcon id={event.id} key={`overtime-icon_${event.id}`} />);
        }

        const iconsElement = <div className={'eventIcons'}> {icons} </div>;

        return icons.length > 0 ? iconsElement : <></>
    }

    const isResourceUnassigned = () => {
        if (isDragging) {
            return actualEventResourceId === "0" || !actualEventResourceId;
        } else {
            return shift?.resourceId === "0"
        }
    }

    const isOvertime = () => {
        let shiftDurationInHour = ((shift?.shiftLength ?? 0) / 60);
        if (isDragging) {
            return (resourceData.allSitesHoursWorked + shiftDurationInHour) > resourceData.maxHoursAllowedPerPayroll ||
                shiftDurationInHour > resourceData.maxHoursPerShift;
        } else {
            return shift?.isOverTime || resourceData.allSitesHoursWorked > resourceData.maxHoursAllowedPerPayroll ||
                (resourceData?.maxHoursPerShift && shiftDurationInHour > resourceData?.maxHoursPerShift);
        }
    }

    const getActiveAlertMessages = (): string[] => {
        const linkedErrors = shift?.linkedErrors ?? [];
        if (linkedErrors.length > 0) {
            return linkedErrors.map((error) => String(error?.message ?? ''));
        }
        return (shift?.errors ?? []).map((message) => String(message ?? ''));
    };

    const hasSleepoverActivePortionMissing = () =>
        getActiveAlertMessages().some((message) =>
            /active portion of (the )?sleepover shift (missing|must be at least)/i.test(message)
        );

    const hasExplicitOvertimeAlert = () => {
        const hasOvertimeAlertText = getActiveAlertMessages()
            .some((message) => /overtime|exceeded max working hours/i.test(message));

        // Keep sleepover active-portion warnings mapped to the bed icon only unless
        // there is explicit overtime wording in the alert text.
        if (hasSleepoverActivePortionMissing() && !hasOvertimeAlertText) {
            return false;
        }

        const shiftDurationInHour = ((shift?.shiftLength ?? 0) / 60);
        const overtimeByResourceCap =
            resourceData.allSitesHoursWorked > resourceData.maxHoursAllowedPerPayroll ||
            (resourceData?.maxHoursPerShift && shiftDurationInHour > resourceData?.maxHoursPerShift);
        const showOvertimeIconSignal = !!shift && (shift.isOverTime || overtimeByResourceCap);

        return hasOvertimeAlertText || showOvertimeIconSignal;
    }

    const isResourceMissingCharacteristics = () => !checkCharacteristics(event.id, actualEventResourceId);

    const isModified = () => {
        if (isDragging) {
            return checkIsModified(shift?.originalResourceID ?? '', actualEventResourceId) === ChangeStatus.Modified;
        } else {
            return event.extendedProps.changeStatus === ChangeStatus.Modified
        }
    }

    const isNotDefined = () => {
        const resource = event.getResources()[0]
        return resource.id === '0';
    }

    const getTimeAsString = (): string => {
        const time = event.extendedProps.shiftLength
        if (time < 60) {
            return `${time} min`
        }
        return `${time / 60} hrs`
    }

    const smartMatchHighlightClass = shift?.smartMatched ? 'smart-match-highlight' : '';

    return (
        <>
        <div
            role={'button'}
            className={`eventContainer ${getStatus()} ${getBorderStyle()} ${smartMatchHighlightClass} ${hasMissingTravelCoordinates ? 'has-missing-travel-coordinates' : ''}`}
            onClick={() => eventClick(event)}
        >
            {event.extendedProps.displayColor && <div className="event-type-color" style={{ backgroundColor: event.extendedProps.displayColor }}>

            </div>}
            {event.extendedProps.isJobBidding && isNotDefined() &&
                <div className={'bellContainer'}>
                    <div className={'bell'}>
                        {event.extendedProps.bidders ? event.extendedProps.bidders.length : 0} <JobBiddingIcon
                            id={event.id} />
                    </div>
                </div>
            }
            <div className={'warning-icons-container'}>
                {getWarningIcons()}
            </div>

            <div className={'eventDescription'}>
                <p>
                    <strong>
                        {format(event.start ?? new Date(), 'h:mma')}
                        &nbsp;-&nbsp;
                        {format(event.end ?? new Date(), 'h:mma')}
                    </strong>
                    <span>
                        &nbsp;{"("}{getTimeAsString()}{")"}
                    </span>
                </p>
                {event.extendedProps.jobType !== JobTypeLabels.ResidentialDisabilityandAgedCareService && (
                    <p>{ event.extendedProps?.serviceContact?.fullName }</p>
                )}
                <p>
                    {event.extendedProps.description && <strong> {truncate(event.extendedProps.description)} </strong>}
                </p>
            </div>
            <div style={{ position: 'absolute', right: '5px', bottom: '0', fontSize: '0.60rem' }}>
                {event.extendedProps.displayCost ? '$' + event.extendedProps.displayCost.toLocaleString(undefined, { maximumFractionDigits: 2 }) : ''}
            </div>


            {/*<div className={"eventIndicator"}/>*/}
        </div>
        </>
    )
}

const OtherSiteEventRender = ({ event }: { event: EventApi }) => {
    const { getEnvironmentInfo } = useContext(shiftsContext)
    const getTimeAsString = (): string => {
        const time = event.extendedProps.shiftLength
        if (time < 60) {
            return `${time} min`
        }
        return `${time / 60} hrs`
    }

    const renderInfo = () => {
        return event.extendedProps.jobType === JobType.ResidentialDisabilityandAgedCareService
            ? event.extendedProps.siteLocation
            : event.extendedProps.customerFullName;
    };

    return (
        <TooltipHost content={'Resource is scheduled at another site.'}>
            <div
                style={{
                    background: '#efefef',
                    height: '50px',
                    margin: '2px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '5px',
                    border: '1px dotted #808080',
                    userSelect: 'none',
                    position: 'relative',
                }}
            >
                <IconButton
                    className={'external-link-button'}
                    iconProps={{ iconName: 'OpenInNewTab' }}
                    href={buildCrmUrl(getEnvironmentInfo(), 'msdyn_workorder', event.id)}
                    target={'_blank'}
                />
                <div>
                    <strong>
                        {format(event.start ?? new Date(), 'h:mma')}
                        &nbsp;-&nbsp;
                        {format(event.end ?? new Date(), 'h:mma')}
                    </strong>
                    <span>
                        &nbsp;({" "}
                        {getTimeAsString()}
                        {")"}
                    </span>
                </div>
                <div>{renderInfo()}</div>
                <div>{event.title}</div>
            </div>
        </TooltipHost>
    );
};

export { EventRender, OtherSiteEventRender }
